import React, { useEffect, useState } from "react";
import { useAuthStore } from "../../state/authStore";
import { childrenApi } from "../../api/children";
import { Icon } from "../components/Icon";
import { WebMascot } from "../components/WebMascot";
import { ChildWithProgress } from "../WebShell";

const ALL_CATS = [
  "Numbers",
  "Letters",
  "Colors",
  "Shapes",
  "Social Skills",
  "Language",
  "Bedtime",
  "Creativity",
  "Music",
  "Nature",
];

type Controls = {
  dailyMin: number;
  sessionMin: number;
  bedtime: boolean;
  questions: boolean;
  autoplay: boolean;
  categories: string[];
};

export function WebControls({ child }: { child: ChildWithProgress }) {
  const { accessToken } = useAuthStore();
  const [controls, setControls] = useState<Controls>({
    dailyMin: 60,
    sessionMin: 20,
    bedtime: false,
    questions: true,
    autoplay: false,
    categories: ["Numbers", "Letters", "Colors", "Shapes", "Social Skills", "Language"],
  });
  const [savedAt, setSavedAt] = useState<number | null>(null);

  // Load from backend
  useEffect(() => {
    if (!accessToken || !child) return;
    childrenApi
      .getControls(child.id, accessToken)
      .then((c) => {
        if (!c) return;
        setControls({
          dailyMin: c.dailyMinutes,
          sessionMin: c.sessionMinutes,
          bedtime: c.bedtimeLock,
          questions: c.interactiveQ,
          autoplay: c.autoPlay,
          // categories in DB are lowercased; map back to display labels
          categories: c.categories.map(
            (lc) => ALL_CATS.find((x) => x.toLowerCase() === lc) ?? lc,
          ),
        });
      })
      .catch(() => {});
  }, [accessToken, child]);

  // Persist (debounced via savedAt timestamp shown to user).
  useEffect(() => {
    if (!accessToken || !child) return;
    const t = setTimeout(() => {
      childrenApi
        .updateControls(
          child.id,
          {
            dailyMinutes: controls.dailyMin,
            sessionMinutes: controls.sessionMin,
            interactiveQ: controls.questions,
            bedtimeLock: controls.bedtime,
            autoPlay: controls.autoplay,
            categories: controls.categories.map((c) => c.toLowerCase()),
          },
          accessToken,
        )
        .then(() => setSavedAt(Date.now()))
        .catch(() => {});
    }, 500);
    return () => clearTimeout(t);
  }, [accessToken, child, controls]);

  const set = <K extends keyof Controls>(k: K, v: Controls[K]) =>
    setControls((c) => ({ ...c, [k]: v }));

  const theme = child.theme ?? { color: "#FF8A4C", bg: "#FFE2C2" };

  return (
    <div
      className="fadeup w-respgrid w-resppad"
      style={{ padding: 32, display: "grid", gridTemplateColumns: "1fr 360px", gap: 24 }}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
        <div className="w-panel">
          <h3>Daily limits</h3>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
            <div className="name" style={{ fontWeight: 700, color: "var(--ink)" }}>
              Total minutes per day
            </div>
            <div
              style={{
                fontFamily: "var(--display)",
                fontSize: 28,
                fontWeight: 900,
                color: "var(--primary-ink)",
              }}
            >
              {controls.dailyMin} min
            </div>
          </div>
          <input
            type="range"
            min={15}
            max={180}
            step={5}
            value={controls.dailyMin}
            onChange={(e) => set("dailyMin", +e.target.value)}
            className="w-slider"
            style={{ marginTop: 14 }}
          />
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              fontSize: 11,
              color: "var(--ink-3)",
              fontWeight: 700,
              marginTop: 6,
            }}
          >
            <span>15m</span>
            <span>60m</span>
            <span>180m</span>
          </div>

          <div
            style={{
              marginTop: 22,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "baseline",
            }}
          >
            <div className="name" style={{ fontWeight: 700, color: "var(--ink)" }}>
              Maximum per session
            </div>
            <div
              style={{
                fontFamily: "var(--display)",
                fontSize: 28,
                fontWeight: 900,
                color: "var(--primary-ink)",
              }}
            >
              {controls.sessionMin === 0 ? "No limit" : `${controls.sessionMin} min`}
            </div>
          </div>
          <input
            type="range"
            min={5}
            max={60}
            step={5}
            value={controls.sessionMin || 5}
            onChange={(e) => set("sessionMin", +e.target.value)}
            className="w-slider"
            style={{ marginTop: 14 }}
          />
        </div>

        <div className="w-panel">
          <h3>Bedtime &amp; behavior</h3>
          <div className="w-row">
            <div>
              <div className="name">Lock Kidly overnight</div>
              <div className="desc">
                Kidly rests from 8:00 PM to 7:00 AM. Parent unlock available.
              </div>
            </div>
            <button
              className={"w-switch " + (controls.bedtime ? "on" : "")}
              onClick={() => set("bedtime", !controls.bedtime)}
            />
          </div>
          <div className="w-row">
            <div>
              <div className="name">Quick questions during videos</div>
              <div className="desc">
                Pause once per video for an age-appropriate question.
              </div>
            </div>
            <button
              className={"w-switch " + (controls.questions ? "on" : "")}
              onClick={() => set("questions", !controls.questions)}
            />
          </div>
          <div className="w-row">
            <div>
              <div className="name">Autoplay next video</div>
              <div className="desc">
                When on, the next video plays automatically. Off by default for new profiles.
              </div>
            </div>
            <button
              className={"w-switch " + (controls.autoplay ? "on" : "")}
              onClick={() => set("autoplay", !controls.autoplay)}
            />
          </div>
        </div>

        <div className="w-panel">
          <h3>What {child.name} can watch</h3>
          <div style={{ fontSize: 13, fontWeight: 500, color: "var(--ink-2)", marginBottom: 14 }}>
            Tap to allow or hide categories. Hidden categories never show up.
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {ALL_CATS.map((c) => {
              const on = controls.categories.includes(c);
              return (
                <button
                  key={c}
                  className={"w-catchip " + (on ? "active" : "")}
                  onClick={() =>
                    set(
                      "categories",
                      on
                        ? controls.categories.filter((x) => x !== c)
                        : [...controls.categories, c],
                    )
                  }
                >
                  {on && <Icon name="check" size={14} />} {c}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <aside style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <div className="w-panel">
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
            <div
              style={{
                width: 56,
                height: 56,
                borderRadius: "50%",
                background: theme.bg,
                overflow: "hidden",
                display: "grid",
                placeItems: "center",
              }}
            >
              <div style={{ marginTop: 10 }}>
                <WebMascot mood="happy" color={theme.color} size={48} />
              </div>
            </div>
            <div>
              <div
                style={{
                  fontFamily: "var(--display)",
                  fontWeight: 900,
                  fontSize: 22,
                  color: "var(--ink)",
                }}
              >
                {child.name}
              </div>
              <div
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: "var(--ink-3)",
                }}
              >
                {child.age} years old · {controls.categories.length} categories
              </div>
            </div>
          </div>
          <div
            style={{
              fontSize: 12,
              fontWeight: 700,
              color: savedAt ? "var(--success)" : "var(--ink-3)",
            }}
          >
            {savedAt ? "Saved" : "Auto-saves as you change"}
          </div>
        </div>
      </aside>
    </div>
  );
}

export default WebControls;
